import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Heart, Star, Users, Zap, Gift, ArrowRight, Loader2, Building2, Car, Target } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import logger from "@/lib/logger";
import Marquee from "react-fast-marquee";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const donationTiers = [
  { id: "visionary", amount: 10000, name: "Visionary", description: "Transform communities", icon: Star, accent: true },
  { id: "empower", amount: 5000, name: "Empower", description: "Build futures", icon: Users },
  { id: "impact", amount: 1000, name: "Impact", description: "Change lives", icon: Zap },
  { id: "support", amount: 500, name: "Support", description: "Scholarship fund", icon: Heart },
  { id: "grow", amount: 250, name: "Grow", description: "Fresh Start Sponsor", icon: Gift },
  { id: "feed", amount: 100, name: "Feed", description: "Feed 10 community members", icon: Heart },
];

// Donation Goals Data (fetched from backend, with fallback defaults)
const defaultGoals = [
  {
    id: "vehicle",
    name: "Vehicle Fund",
    description: "Help us transport youth to events, college visits, and job fairs",
    icon: "Car",
    target: 35000,
    current: 0,
    color: "soar-teal",
  },
  {
    id: "building",
    name: "Building Fund",
    description: "Help us secure a permanent home for SOAR Youth Center",
    icon: "Building2",
    target: 100000,
    current: 0,
    color: "soar-gold",
  },
];

const getIconComponent = (iconName) => {
  const icons = { Car, Building2, Target };
  return icons[iconName] || Target;
};

const DonationGoalCard = ({ goal }) => {
  const percentage = Math.min((goal.current / goal.target) * 100, 100);
  const Icon = getIconComponent(goal.icon);
  const isTeal = goal.color === "soar-teal";
  
  return (
    <div className="card-elevated rounded-3xl overflow-hidden group">
      <div className={`p-6 ${isTeal ? 'bg-soar-teal' : 'bg-soar-gold'}`}>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
            <Icon className={`w-7 h-7 ${isTeal ? 'text-white' : 'text-soar-blue'}`} />
          </div>
          <div>
            <h3 className={`font-heading text-2xl tracking-tight ${isTeal ? 'text-white' : 'text-soar-blue'}`}>
              {goal.name}
            </h3>
            <p className={`text-sm ${isTeal ? 'text-white/70' : 'text-soar-blue/70'}`}>
              {goal.description}
            </p>
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="flex justify-between items-end mb-3">
          <div>
            <p className="text-soar-blue/50 text-xs font-medium uppercase tracking-wider">Raised</p>
            <p className="font-heading text-3xl text-soar-blue tracking-tight">
              ${goal.current.toLocaleString()}
            </p>
          </div>
          <div className="text-right">
            <p className="text-soar-blue/50 text-xs font-medium uppercase tracking-wider">Goal</p>
            <p className="font-heading text-xl text-soar-blue/60 tracking-tight">
              ${goal.target.toLocaleString()}
            </p>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="relative h-4 bg-soar-blue/10 rounded-full overflow-hidden mb-3">
          <div 
            className={`absolute inset-y-0 left-0 rounded-full transition-all duration-1000 ease-out ${isTeal ? 'bg-soar-teal' : 'bg-soar-gold'}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <div className="flex justify-between items-center text-xs">
          <span className={`font-bold ${isTeal ? 'text-soar-teal' : 'text-soar-gold'}`}>
            {percentage.toFixed(0)}% funded
          </span>
          <span className="text-soar-blue/50">
            ${(goal.target - goal.current).toLocaleString()} still needed
          </span>
        </div>
      </div>
    </div>
  );
};

const Donate = () => {
  const [selectedTier, setSelectedTier] = useState("impact");
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [message, setMessage] = useState("");
  const [processing, setProcessing] = useState(false);
  const [goals, setGoals] = useState(defaultGoals);

  const fetchGoals = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/donation-goals`);
      if (response.data && response.data.length > 0) {
        setGoals(response.data);
      }
    } catch (error) {
      // Use default goals if API fails
      logger.info("Using default donation goals");
    }
  }, []);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  const getFinalAmount = () => {
    if (customAmount && parseFloat(customAmount) > 0) {
      return parseFloat(customAmount);
    }
    if (selectedTier) {
      const tier = donationTiers.find(t => t.id === selectedTier);
      return tier ? tier.amount : 0;
    }
    return 0;
  };

  const handleDonate = async () => {
    const amount = getFinalAmount();
    
    if (!amount || amount <= 0) {
      toast.error("Please select or enter a donation amount");
      return;
    }

    setProcessing(true);

    try {
      const response = await axios.post(`${API}/donate/checkout`, {
        amount: amount,
        origin_url: window.location.origin,
        donor_name: donorName || null,
        donor_email: donorEmail || null,
        message: message || null
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error("Failed to create checkout session");
      }
    } catch (error) {
      logger.error("Donation error:", error);
      toast.error("Failed to process donation. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div data-testid="donate-page" className="bg-white">

      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/6646918/pexels-photo-6646918.jpeg?w=1600"
            alt="Donate"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-soar-blue via-soar-blue/95 to-soar-blue/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <span className="inline-block border border-soar-gold/40 rounded-full px-4 py-1.5 text-xs font-semibold tracking-widest text-soar-gold uppercase mb-6 opacity-0 animate-fade-up" style={{ animationFillMode: 'forwards' }}>
            Support Our Mission
          </span>
          <h1 className="font-heading text-6xl sm:text-7xl lg:text-8xl leading-none tracking-tight text-white mb-6 opacity-0 animate-fade-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
            Make a <span className="text-soar-gold">Donation</span>
          </h1>
          <p className="font-accent text-3xl text-soar-gold mb-4 opacity-0 animate-fade-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
            &ldquo;From your heart to their future&rdquo;
          </p>
          <p className="text-lg text-white/70 max-w-2xl mx-auto opacity-0 animate-fade-up" style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
            Your donation supports youth confidence, skills, and self-worth.
            Together, we empower youth to think, build, and dream.
          </p>
        </div>
      </section>

      {/* Marquee */}
      <div className="py-5" style={{ backgroundColor: '#212163' }}>
        <Marquee speed={50} gradient={false}>
          {["GIVE", "SUPPORT", "TRANSFORM", "EMPOWER", "INSPIRE", "UPLIFT"].map((w) => (
            <span key={w} className="font-heading text-4xl mx-12 tracking-wider" style={{ color: 'rgba(221,182,77,0.3)' }}>{w}</span>
          ))}
        </Marquee>
      </div>

      {/* Donation Form */}
      <section className="py-24 bg-white" data-testid="donation-form-section">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-8">

            {/* Amount Selection */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <h2 className="font-heading text-3xl text-soar-blue mb-2 tracking-tight">Choose Your Impact</h2>
                <p className="text-soar-blue/50 text-sm">Select a giving level or enter a custom amount.</p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {donationTiers.map((tier) => (
                  <button
                    key={tier.id}
                    onClick={() => {
                      setSelectedTier(tier.id);
                      setCustomAmount("");
                    }}
                    className={`card-elevated rounded-2xl p-5 text-center group transition-all duration-200 ${
                      selectedTier === tier.id && !customAmount 
                        ? 'ring-2 ring-soar-gold border-soar-gold/50' 
                        : 'hover:border-soar-blue/20'
                    } ${tier.accent ? 'relative' : ''}`}
                  >
                    {tier.accent && (
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-soar-gold text-[#0a0a2e] text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                        Most Impact
                      </div>
                    )}
                    <div className={`w-12 h-12 mx-auto mb-3 rounded-2xl ${selectedTier === tier.id && !customAmount ? 'bg-soar-gold/20' : 'bg-soar-blue/5'} flex items-center justify-center group-hover:bg-soar-gold/10 transition-colors`}>
                      <tier.icon className={`w-6 h-6 ${selectedTier === tier.id && !customAmount ? 'text-soar-gold' : 'text-soar-blue/50'}`} aria-hidden="true" />
                    </div>
                    <p className="font-heading text-sm text-soar-blue mb-1">{tier.name}</p>
                    <p className="font-heading text-2xl text-soar-teal tracking-tight">${tier.amount.toLocaleString()}</p>
                    <p className="text-[11px] text-soar-blue/50 mt-1">{tier.description}</p>
                  </button>
                ))}
              </div>

              {/* Custom Amount */}
              <div className="card-elevated rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-soar-teal/15 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-soar-teal" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="font-heading text-soar-blue text-sm">Think For Self</p>
                    <p className="text-xs text-white/30">Enter any amount that feels right</p>
                  </div>
                </div>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl font-bold text-white/30">$</span>
                  <Input
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder="Enter amount"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      if (e.target.value) setSelectedTier(null);
                    }}
                    className="pl-10 rounded-xl py-6 text-xl font-heading bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20"
                    data-testid="custom-amount-input"
                  />
                </div>
              </div>

              {/* Donor Info */}
              <div className="space-y-4 pt-4 border-t border-soar-blue/10">
                <h3 className="font-heading text-lg text-soar-blue">Donor Information (Optional)</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="donorName" className="text-soar-blue/70">Name</Label>
                    <Input
                      id="donorName"
                      placeholder="Your name"
                      value={donorName}
                      onChange={(e) => setDonorName(e.target.value)}
                      className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20"
                      data-testid="donor-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="donorEmail" className="text-soar-blue/70">Email</Label>
                    <Input
                      id="donorEmail"
                      type="email"
                      placeholder="your@email.com"
                      value={donorEmail}
                      onChange={(e) => setDonorEmail(e.target.value)}
                      className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20"
                      data-testid="donor-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-soar-blue/70">Message (Optional)</Label>
                  <Textarea
                    id="message"
                    placeholder="Leave a message of support..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20"
                    data-testid="donor-message-input"
                  />
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="lg:col-span-1">
              <div className="card-elevated rounded-3xl p-6 sticky top-28">
                <h3 className="font-heading text-xl text-soar-blue mb-6 tracking-tight">Donation Summary</h3>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center py-3 border-b border-soar-blue/10">
                    <span className="text-white/30 text-sm">Amount</span>
                    <span className="font-heading text-4xl text-soar-teal tracking-tight">
                      ${getFinalAmount().toLocaleString()}
                    </span>
                  </div>
                  {donorName && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-white/30">From</span>
                      <span className="text-soar-blue font-medium">{donorName}</span>
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleDonate}
                  disabled={processing || !getFinalAmount()}
                  className="w-full rounded-full bg-soar-gold hover:bg-soar-gold/90 text-[#0a0a2e] py-6 text-base font-bold hover:scale-[1.02] transition-transform duration-200 shadow-[0_0_30px_rgba(221,182,77,0.2)]"
                  data-testid="donate-submit-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Donate ${getFinalAmount().toLocaleString()}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-[11px] text-white/20 text-center mt-4">
                  Secure payment powered by Stripe.
                  S.O.A.R Solutions is a 501(c)(3) nonprofit organization.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Donation Goals with Progress */}
      <section className="py-24 bg-gradient-to-b from-white to-soar-teal/5" data-testid="donation-goals">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Target className="w-5 h-5 text-soar-gold" />
              <span className="text-soar-gold text-xs font-semibold tracking-widest uppercase">Fundraising Goals</span>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl text-soar-blue tracking-tight">Help Us Reach Our Goals</h2>
            <p className="text-soar-blue/50 mt-4 max-w-2xl mx-auto">
              Track our progress and contribute to specific initiatives that will expand our reach and impact.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {goals.map((goal) => (
              <DonationGoalCard key={goal.id} goal={goal} />
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-soar-blue/50 text-sm">
              Want to donate specifically to one of these funds? Mention it in your donation message above.
            </p>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-soar-teal" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-soar-blue mb-4 tracking-tight">Trusted by Our Community</h2>
          <p className="text-soar-blue/80 mb-8">
            S.O.A.R Solutions Youth Center is a registered 501(c)(3) nonprofit organization.
            Your donation is tax-deductible to the fullest extent allowed by law.
          </p>
          <div className="flex flex-wrap justify-center gap-8 text-sm text-soar-blue/60 font-semibold">
            <span>Secure Payments</span>
            <span>Tax Deductible</span>
            <span>100% Goes to Programs</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;
